"use client";

import { useIsomorphicLayoutEffect } from "@/hooks/use-isomorphic-layout-effect";
import { EASE_OUT } from "@/lib/ease";
import { cn } from "@/lib/utils";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Transition,
} from "motion/react";
import { useRef, useState, type ReactNode, type Ref } from "react";
import { WORKSPACE_DEVICES } from "@/config/layout";
import type { WorkspaceDevice } from "@/config/layout";

const STAGE_PADDING = 40;

const MORPH_TRANSITION: Transition = { duration: 0.36, ease: EASE_OUT };
const INSTANT_TRANSITION: Transition = { duration: 0 };

export function deviceFrameSize(device: WorkspaceDevice) {
  const spec = WORKSPACE_DEVICES[device];
  return {
    width: spec.width + spec.bezel * 2,
    height: spec.height + spec.bezel * 2,
  };
}

export interface DeviceFrameProps {
  device: WorkspaceDevice;
  scale?: number;
  children?: ReactNode;
  contentRef?: Ref<HTMLDivElement>;
  className?: string;
}

export function DeviceFrame({
  device,
  scale = 1,
  children,
  contentRef,
  className,
}: DeviceFrameProps) {
  const reduceMotion = useReducedMotion();
  const transition = reduceMotion ? INSTANT_TRANSITION : MORPH_TRANSITION;
  const spec = WORKSPACE_DEVICES[device];
  const frame = deviceFrameSize(device);

  return (
    <motion.div
      initial={false}
      animate={{ width: frame.width * scale, height: frame.height * scale }}
      transition={transition}
      className={cn("relative", className)}
    >
      <motion.div
        initial={false}
        animate={{
          width: frame.width,
          height: frame.height,
          scale,
          borderRadius: spec.radius + spec.bezel,
          padding: spec.bezel,
        }}
        transition={transition}
        style={{ originX: 0, originY: 0 }}
        className="absolute left-0 top-0 bg-background shadow-[0_24px_60px_-24px_rgba(0,0,0,0.25)] ring-1 ring-neutral-200 dark:ring-neutral-800"
      >
        <motion.div
          initial={false}
          animate={{ borderRadius: spec.radius }}
          transition={transition}
          data-handyout-theme="follow"
          className="relative h-full w-full overflow-hidden bg-background ring-1 ring-neutral-200 dark:ring-neutral-800"
        >
          <div
            ref={contentRef}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ width: spec.width, height: spec.height }}
          >
            {children}
          </div>
          <AnimatePresence initial={false}>
            {device === "phone" && (
              <motion.div
                key="notch"
                aria-hidden
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={transition}
                className="pointer-events-none absolute left-0 right-0 top-0 z-30 flex flex-row items-center justify-center gap-1 pt-2"
              >
                <div className="h-4 w-14 rounded-full border border-neutral-300 dark:border-neutral-700" />
                <div className="size-4 rounded-full border border-neutral-300 dark:border-neutral-700" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

export interface DeviceStageProps {
  device: WorkspaceDevice;
  children?: ReactNode;
  contentRef?: Ref<HTMLDivElement>;
  deviceRef?: Ref<HTMLDivElement>;
  renderOverlay?: (scale: number) => ReactNode;
  renderStageOverlay?: (scale: number) => ReactNode;
  className?: string;
  dataTour?: string;
}

export function DeviceStage({
  device,
  children,
  contentRef,
  deviceRef,
  renderOverlay,
  renderStageOverlay,
  className,
  dataTour,
}: DeviceStageProps) {
  const wellRef = useRef<HTMLDivElement>(null);
  const [available, setAvailable] = useState<{ width: number; height: number } | null>(
    null,
  );

  useIsomorphicLayoutEffect(() => {
    const element = wellRef.current;
    if (!element) return;
    const measure = () => {
      const rect = element.getBoundingClientRect();
      setAvailable((prev) =>
        prev && prev.width === rect.width && prev.height === rect.height
          ? prev
          : { width: rect.width, height: rect.height },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const frame = deviceFrameSize(device);
  const scale = available
    ? Math.max(
        0.1,
        Math.min(
          WORKSPACE_DEVICES[device].maxScale,
          (available.width - STAGE_PADDING * 2) / frame.width,
          (available.height - STAGE_PADDING * 2) / frame.height,
        ),
      )
    : WORKSPACE_DEVICES[device].maxScale;

  return (
    <div
      ref={wellRef}
      className={cn("relative min-h-0 min-w-0 flex-1 overflow-hidden", className)}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <div ref={deviceRef} data-tour={dataTour}>
          <DeviceFrame device={device} scale={scale} contentRef={contentRef}>
            {children}
            {renderOverlay?.(scale)}
          </DeviceFrame>
        </div>
        {renderStageOverlay?.(scale)}
      </div>
      {scale < 0.999 && (
        <span className="absolute bottom-3 right-3 rounded-md bg-background/90 px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground ring-1 ring-border">
          {Math.round(scale * 100)} %
        </span>
      )}
    </div>
  );
}
